import { Button, InlineMessage, Loader } from "@saviynt/design-system";
import { FormattedMessage, useIntl } from "react-intl";
import { getCertificateFileName } from "../utils";
import CertificateCard from "./CertificateCard";
import "./CertificateSection.css";

interface CertificateSectionProps {
  /** Base64-encoded signing certificate */
  signingCert: string | null | undefined;
  /** Base64-encoded encryption certificate */
  encryptionCert: string | null | undefined;
  /** Identity Provider name used for filename generation */
  idpName: string;
  /** Current SSO configuration status */
  status: "TEST" | "ENABLED" | "DISABLED";
  /** Whether the section is in a loading state */
  isLoading?: boolean;
  /** Error message to display when config refetch fails */
  fetchError?: string | null;
  /** Callback to retry fetching the config */
  onRetry?: () => void;
}

/**
 * Displays certificate download cards for signing and encryption certificates.
 * Shows a pending activation warning when status is TEST.
 * Hidden when both certificates are null (pre-save state).
 */
export default function CertificateSection({
  signingCert,
  encryptionCert,
  idpName,
  status,
  isLoading = false,
  fetchError,
  onRetry,
}: CertificateSectionProps) {
  const intl = useIntl();

  // Show error state when config refetch fails
  if (fetchError) {
    return (
      <div className="CertSection">
        <div aria-live="polite">
          <InlineMessage colorTheme="critical" text={fetchError} size="small" />
        </div>
        {onRetry && (
          <div style={{ marginTop: "8px" }}>
            <Button kind="outlined" size="small" onClick={onRetry}>
              <FormattedMessage
                id="P+08d5"
                defaultMessage="Retry"
                description="Button to retry fetching SSO configuration"
              />
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Hidden when both certs are null
  if (!signingCert && !encryptionCert && !isLoading) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="CertSection">
        <div className="CertSection__loading">
          <Loader kind="loop" format="main" size="medium" color="brand" />
        </div>
      </div>
    );
  }

  const signingFileName = getCertificateFileName(idpName, "signing");
  const encryptionFileName = getCertificateFileName(idpName, "encryption");

  return (
    <div className="CertSection">
      {/* Pending activation warning */}
      {status === "TEST" && (
        <div aria-live="polite">
          <InlineMessage
            colorTheme="warning"
            text={intl.formatMessage({
              id: "GMuq47",
              defaultMessage:
                "Your SSO configuration is in Pilot mode. Both SSO and password login are active. Download the certificates below and upload them to your Identity Provider, then test the connection.",
              description:
                "Warning message shown when SSO is in pilot/test state",
            })}
          />
        </div>
      )}

      {/* Certificate cards */}
      <div className="CertSection__cards">
        <CertificateCard
          label={intl.formatMessage({
            id: "0wnwYt",
            defaultMessage: "Signing Certificate",
            description: "Label for the signing certificate card",
          })}
          certBase64={signingCert}
          fileName={signingFileName}
        />
        <CertificateCard
          label={intl.formatMessage({
            id: "bEdQnK",
            defaultMessage: "Encryption Certificate",
            description: "Label for the encryption certificate card",
          })}
          certBase64={encryptionCert}
          fileName={encryptionFileName}
        />
      </div>
    </div>
  );
}
